/**
 * EndGameModal — transparent dismissable overlay shown when a session
 * transitions to COMPLETE. The map stays visible underneath while the modal
 * presents the result, profile-linked standings, Dice Luck, and routes to the
 * final map or lobby.
 *
 * Two states: a clean single-winner victory and a stalled-with-tie
 * outcome (`winnerSlot=null`, `tiedSlots` populated). The caller
 * decides which to render based on the engine's computeStalledWinner
 * output already encoded in the game payload.
 */

import React from 'react'
import { View, Text, TouchableOpacity, Pressable, ScrollView } from 'react-native'
import { useRouter } from 'expo-router'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { PLAYER_COLORS, PlayerColorChip, clampPlayerSlot } from '@warcarp/ui'
import { useColorBySlot } from '../../lib/playerColor'
import { playerProfilePath } from '../../lib/botProfileLink'
import { teamLabel, type GameResultSnapshotPlacement, type TeamId } from '@warcarp/api-types'
import { TeamShape } from './TeamShape'
import { teamStandings } from '../../lib/winner'
import { GameOverlayTopContext, inGameBackdropBounds } from '../../lib/gameOverlayBounds'

interface Props {
  winnerName?: string
  winnerSlot?: number
  /** When set, the game ended on a tie — render the names instead of a single victor. */
  tied?: { name: string; slot: number }[]
  /** Ordered immutable standings from the completion snapshot. */
  placements?: GameResultSnapshotPlacement[]
  /**
   * Team games: the winning team and every member (eliminated members
   * included, resigned members not). Replaces the single winner with "Team B wins!".
   */
  winningTeam?: { team: TeamId; members: { name: string; slot: number }[] }
  onShowDiceLuck?: () => void
  onClose: () => void
}

function placeLabel(place: number): string {
  const suffix = place % 100 >= 11 && place % 100 <= 13
    ? 'th'
    : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[place % 10] ?? 'th'
  const medal = ({ 1: '🥇 ', 2: '🥈 ', 3: '🥉 ' } as Record<number, string>)[place] ?? ''
  return `${medal}${place}${suffix}`
}

function CompletedPlayerName({
  placement,
  displayName,
  textClassName,
  containerClassName,
  numberOfLines,
}: {
  placement?: GameResultSnapshotPlacement
  displayName: string
  textClassName: string
  containerClassName?: string
  numberOfLines?: number
}) {
  const router = useRouter()
  const href = placement ? playerProfilePath(placement) : null
  const label = (
    <Text
      className={`${textClassName}${href ? ' underline' : ''}`}
      numberOfLines={numberOfLines}
    >
      {displayName}
    </Text>
  )
  if (!href) return <View className={containerClassName}>{label}</View>
  return (
    <TouchableOpacity
      onPress={() => router.push(href)}
      accessibilityLabel={`View ${displayName}'s profile`}
      accessibilityRole="link"
      className={containerClassName}
    >
      {label}
    </TouchableOpacity>
  )
}

export function EndGameModal({ winnerName, winnerSlot, tied, placements, winningTeam, onShowDiceLuck, onClose }: Props) {
  const router = useRouter()
  const protectedTop = React.useContext(GameOverlayTopContext)
  // seat slot → chosen color slot (identity fallback outside a game context).
  const colorBySlot = useColorBySlot()
  const colorSlotFor = (s: number) => clampPlayerSlot(colorBySlot[s] ?? s)
  const placementForSlot = (slot: number) => placements?.find((placement) => placement.slot === slot)
  const standings = winningTeam ? teamStandings(placements) : []
  // Team games: each member's chip is drawn in the team's shape, in the
  // player's colour (the same mark as the dot strip and the drawer).
  const teamChip = (team: TeamId, slot: number, size: number) => (
    <TeamShape key={slot} team={team} size={size} fill={PLAYER_COLORS[colorSlotFor(slot)].hex}
      stroke="rgba(0,0,0,0.25)" strokeWidth={1} testID={`end-game-team-chip-${slot}`} />
  )

  return (
    // Tapping the dark backdrop dismisses (same as View Map — reveal the
    // final board underneath). The inner card absorbs taps so taps on it
    // don't bubble to the backdrop.
    //
    // accessible={false} is load-bearing. This Pressable used to carry
    // accessibilityLabel="Dismiss game-over banner", which makes iOS treat the
    // whole subtree as ONE accessibility element — so "View Map", "Lobby" and
    // the × close button were unreachable by VoiceOver (and invisible to UI
    // tests, which is how it was found). The card's own buttons are the
    // accessible way out; the backdrop stays a convenience for sighted users.
    <Pressable
      accessible={false}
      onPress={onClose}
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        ...inGameBackdropBounds(protectedTop, 24),
        backgroundColor: 'rgba(0,0,0,0.55)',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 30,
        paddingHorizontal: 24,
        paddingBottom: 24,
        overflow: 'hidden',
      }}
    >
      <Pressable
        // Also accessible={false}: a Pressable is an accessibility element by
        // DEFAULT on iOS, so this tap-absorbing wrapper collapsed the card into
        // one node too — VoiceOver read the whole card as a single string
        // ("GAME OVER, … , View the final map, Back to lobby, Close") with no
        // way to activate any individual button. It exists only to stop taps
        // bubbling to the backdrop; it is not a control.
        accessible={false}
        onPress={() => {}}
        className="bg-paper border-2 border-olive rounded-2xl px-6 py-6 items-center"
        style={{ maxWidth: 420, width: '100%', maxHeight: '100%' }}
      >
        {/* Close (×) — upper-right corner of the card. */}
        <TouchableOpacity
          onPress={onClose}
          accessibilityLabel="Close"
          accessibilityRole="button"
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          style={{ position: 'absolute', top: 8, right: 12, padding: 4, zIndex: 1 }}
        >
          <Text className="text-ink-soft font-stencil text-xl">×</Text>
        </TouchableOpacity>

        <ScrollView style={{ width: '100%' }} contentContainerStyle={{ alignItems: 'center', gap: 12 }}>
        <Text className="text-ink-soft text-xs uppercase tracking-widest font-stencil">
          Game Over
        </Text>
        {winningTeam ? (
          <>
            <View className="flex-row items-center justify-center gap-1.5" testID="end-game-team-chips">
              {winningTeam.members.map((m) => teamChip(winningTeam.team, m.slot, 52))}
            </View>
            <Text className="text-ink text-2xl font-stencil text-center uppercase" testID="end-game-team-winner">
              {teamLabel(winningTeam.team)} wins!
            </Text>
            <View className="flex-row flex-wrap items-center justify-center gap-x-3 gap-y-1">
              {winningTeam.members.map((m) => (
                <View key={m.slot} className="flex-row items-center gap-1.5">
                  {teamChip(winningTeam.team, m.slot, 22)}
                  <CompletedPlayerName
                    placement={placementForSlot(m.slot)}
                    displayName={m.name}
                    textClassName="text-ink font-typed text-base"
                  />
                </View>
              ))}
            </View>
          </>
        ) : winnerName && winnerSlot !== undefined ? (
          <>
            <PlayerColorChip slot={colorSlotFor(winnerSlot)} size="lg" showLabel />
            <View className="flex-row flex-wrap items-center justify-center gap-2">
              <CompletedPlayerName
                placement={placementForSlot(winnerSlot)}
                displayName={winnerName}
                textClassName="text-ink text-2xl font-stencil text-center uppercase"
              />
              <Text className="text-ink text-2xl font-stencil text-center uppercase">wins!</Text>
            </View>
          </>
        ) : tied && tied.length > 0 ? (
          <>
            <Text className="text-ink text-lg font-stencil text-center uppercase">
              Tie game
            </Text>
            <View className="flex-row flex-wrap items-center justify-center gap-3">
              {tied.map((t) => (
                <View key={t.slot} className="flex-row items-center gap-1.5">
                  <PlayerColorChip slot={colorSlotFor(t.slot)} size="sm" />
                  <CompletedPlayerName
                    placement={placementForSlot(t.slot)}
                    displayName={t.name}
                    textClassName="text-ink font-typed text-sm"
                  />
                </View>
              ))}
            </View>
          </>
        ) : (
          <Text className="text-ink text-lg font-stencil text-center uppercase">No winner</Text>
        )}
        {standings.length > 0 ? (
          <View className="self-stretch mt-1 border-t border-olive pt-3 items-center" testID="end-game-team-standings">
            <Text className="text-ink-soft text-xs uppercase tracking-widest font-stencil text-center mb-2">
              Final standings
            </Text>
            <View style={{ width: '100%', maxWidth: 300 }}>
              {standings.map((row) => (
                <View key={row.team} className="flex-row items-center py-1.5 gap-2">
                  <Text className="text-ink-soft font-stencil text-xs w-16 text-right">
                    {placeLabel(row.placement)}
                  </Text>
                  <Text className="text-ink font-stencil text-xs w-16" numberOfLines={1}>{teamLabel(row.team)}</Text>
                  <View className="flex-row items-center gap-1">
                    {row.members.map((m) => teamChip(row.team, m.slot, 22))}
                  </View>
                  <Text className="text-ink font-typed text-sm flex-1" numberOfLines={2}>
                    {row.members.map((m) => m.displayName).join(', ')}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        ) : placements && placements.length > 0 && (
          <View className="self-stretch mt-1 border-t border-olive pt-3 items-center">
            <Text className="text-ink-soft text-xs uppercase tracking-widest font-stencil text-center mb-2">
              Final standings
            </Text>
            <ScrollView
              style={{ maxHeight: 176, width: '100%' }}
              contentContainerStyle={{ alignItems: 'center' }}
            >
              <View style={{ width: '100%', maxWidth: 260 }}>
                {placements.slice(0, 3).map((placement) => (
                  <View key={`${placement.slot}-${placement.placement}`} className="flex-row items-center py-1.5 gap-2">
                    <Text className="text-ink-soft font-stencil text-xs w-28 text-right">
                      {placeLabel(placement.placement)}
                    </Text>
                    <View className="w-10 items-center">
                      <PlayerColorChip slot={colorSlotFor(placement.slot)} size="sm" />
                    </View>
                    <CompletedPlayerName
                      placement={placement}
                      displayName={placement.displayName}
                      textClassName="text-ink font-typed text-sm"
                      containerClassName="flex-1"
                      numberOfLines={1}
                    />
                  </View>
                ))}
              </View>
            </ScrollView>
          </View>
        )}
        {onShowDiceLuck && (
          <TouchableOpacity
            // Close this modal first so Dice Luck's backdrop replaces ours
            // instead of darkening the screen a second time.
            onPress={() => { onClose(); onShowDiceLuck() }}
            accessibilityLabel="View dice luck"
            accessibilityRole="button"
            className="mt-1 self-stretch flex-row items-center justify-center gap-2 bg-paper-up border border-olive rounded-xl px-6 py-2.5"
            style={{ minHeight: 44 }}
          >
            <MaterialCommunityIcons name="dice-multiple-outline" size={20} color="#8a8c6e" />
            <Text numberOfLines={1} className="text-ink font-stencil uppercase">Dice Luck</Text>
          </TouchableOpacity>
        )}
        {/* self-stretch: the card is items-center, so without it this row
            would shrink to content width and the flex-1 buttons would only
            get their natural size — clipping "View Map" to "View …". Stretching
            to the full card width lets the two flex-1 buttons split it evenly,
            so both are the same width and each label fits on one line. */}
        <View className="mt-2 flex-row gap-3 self-stretch">
          <TouchableOpacity
            onPress={onClose}
            accessibilityLabel="View the final map"
            accessibilityRole="button"
            className="flex-1 bg-paper-up border border-olive rounded-xl px-6 py-2.5 items-center justify-center"
            style={{ minHeight: 44 }}
          >
            <Text numberOfLines={1} className="text-ink font-stencil uppercase">
              View Map
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => router.replace('/(app)')}
            accessibilityLabel="Back to lobby"
            accessibilityRole="button"
            className="flex-1 bg-paper-up border border-olive rounded-xl px-6 py-2.5 items-center justify-center"
            style={{ minHeight: 44 }}
          >
            <Text numberOfLines={1} className="text-ink font-stencil uppercase">
              Lobby
            </Text>
          </TouchableOpacity>
        </View>
        </ScrollView>
      </Pressable>
    </Pressable>
  )
}
